import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { Button, Switch, Tag } from 'antd'

import { DeleteOutlined, SlidersOutlined } from '@ant-design/icons'
import { HotTable } from '@handsontable/react'
import { textRenderer } from 'handsontable/renderers'

import { LabelSettings } from '@/components/imagemap/right-panel/data-source/visualSettingsAfterRemap'

import { isNumeric } from '../helpers'
import styles from './Table.module.css'

const { CheckableTag } = Tag

type Props = {
  data: string[][]
  rowHeaders: string[]
  colHeaders: string[]
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  setRightIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  clearDataSelection: () => void
  onSubmit?: ([row, col]: [number, number]) => void
  showTransform?: boolean
  // Table shapes only: toggles that hide the row/column labels (issue #2466).
  showLabelToggles?: boolean
  labelSettings?: LabelSettings
  onLabelSettingsChange?: (labels: LabelSettings) => Promise<void>
  modern?: boolean
}

const pluralize = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`

function modernRenderer(instance, td, row, col, prop, value, cellProperties) {
  textRenderer.apply(this, arguments)
  td.classList.toggle('is-number', isNumeric(value))
}

const OutputTable = ({
  setIsLoading,
  data,
  rowHeaders: allRowHeaders,
  colHeaders: allColHeaders,
  setRightIsOpen,
  clearDataSelection,
  onSubmit = null,
  showTransform = true,
  showLabelToggles = false,
  labelSettings,
  onLabelSettingsChange,
  modern = false
}: Props) => {
  const history = useHistory()
  const hasData = data != null
  const tableRef = useRef(null)
  // A missing flag means the labels are shown, as on the slide.
  const labels = {
    showRowHeaders: !showLabelToggles || labelSettings?.showRowHeaders !== false,
    showColHeaders: !showLabelToggles || labelSettings?.showColHeaders !== false
  }
  const rowHeaders = labels.showRowHeaders ? allRowHeaders : false
  const colHeaders = labels.showColHeaders ? allColHeaders : false
  const [savingLabels, setSavingLabels] = useState(false)
  const [selection, setSelection] = useState<[number, number]>()

  const changeLabels = async (next: LabelSettings) => {
    setSavingLabels(true)
    try {
      await onLabelSettingsChange(next)
    } catch (error) {
      console.error(`An error occurred while saving label visibility: ${error.message}`)
    } finally {
      setSavingLabels(false)
    }
  }
  const afterSelection = (row1, col1, row2, col2) => {
    setSelection([row1, col1])
  }

  // `@handsontable/react` does not reactively re-render when the `data`/`rowHeaders`/
  // `colHeaders` props change, so a fresh selection updated the reducer state but left the
  // already-mounted table showing the previous selection (it only refreshed on remount, i.e.
  // "Back to slides"). Push the new values into the live instance the same way the Input
  // table does. (issue: data view select not reflected in Output table)
  useEffect(() => {
    if (!hasData) {
      return
    }
    const hot = tableRef.current?.hotInstance
    if (hot && !hot.isDestroyed) {
      hot.updateSettings({ data, rowHeaders, colHeaders })
    }
  }, [data, rowHeaders, colHeaders, hasData])

  const submit = async () => {
    // Only forward a selection when the user actually picked an output cell
    // (row/column dynamic-mapping). For a scalar value there is nothing to pick, so
    // `selection` is undefined — calling onSubmit then throws and the redirect below
    // never runs. Either way, submitting returns the user to the slides.
    if (onSubmit != null && selection != null) {
      await onSubmit(selection)
    }
    history.goBack()
  }

  if (modern) {
    const canToggleLabels = hasData && showLabelToggles && onLabelSettingsChange
    return (
      <section className="workbench-card output-card">
        <div className="card-head">
          <h3>Output table</h3>
          {hasData && (
            <span className="muted">
              {pluralize(data.length, 'row')} · {pluralize(data[0]?.length || 0, 'column')}
            </span>
          )}
        </div>
        <div className="card-toolbar">
          {canToggleLabels && (
            <div
              className="label-switches"
              style={savingLabels ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
            >
              <span className="toolbar-label">Show</span>
              <label className="switch-field">
                <Switch
                  size="small"
                  checked={labels.showRowHeaders}
                  onChange={checked => changeLabels({ ...labels, showRowHeaders: checked })}
                />
                Row labels
              </label>
              <label className="switch-field">
                <Switch
                  size="small"
                  checked={labels.showColHeaders}
                  onChange={checked => changeLabels({ ...labels, showColHeaders: checked })}
                />
                Column labels
              </label>
            </div>
          )}
          {showTransform && (
            <Button
              className="toolbar-end"
              icon={<SlidersOutlined rotate={90} />}
              disabled={!hasData}
              onClick={() => setRightIsOpen(true)}
            >
              Transform data
            </Button>
          )}
        </div>
        <div className="grid-area">
          {hasData ? (
            <HotTable
              ref={tableRef}
              className="modern-grid"
              readOnly={true}
              readOnlyCellClassName="is-readonly"
              data={data}
              autoRowSize={true}
              licenseKey="non-commercial-and-evaluation"
              width="100%"
              height="100%"
              rowHeaders={rowHeaders}
              colHeaders={colHeaders}
              afterSelection={afterSelection}
              selectionMode="single"
              rowHeaderWidth={160}
              cells={() => ({ renderer: modernRenderer, className: 'cell-value' })}
            />
          ) : (
            <div className="empty-state">No data selected yet.</div>
          )}
        </div>
        <div className="card-foot">
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            disabled={!hasData}
            onClick={clearDataSelection}
          >
            Clear
          </Button>
          <Button disabled={!hasData} type="primary" onClick={submit}>
            Submit selection
          </Button>
        </div>
      </section>
    )
  }

  return (
    <div className="output-table-col">
      <div className="header-section">
        <h3>Output table</h3>
        <Button danger onClick={() => history.goBack()}>
          Back to slides
        </Button>
      </div>
      <div className="table-container">
        {hasData ? (
          <HotTable
            ref={tableRef}
            readOnly={true}
            readOnlyCellClassName={styles.readonly}
            data={data}
            autoRowSize={true}
            licenseKey="non-commercial-and-evaluation"
            width="100%"
            height="100%"
            rowHeaders={rowHeaders}
            colHeaders={colHeaders}
            afterSelection={afterSelection}
            selectionMode="single"
            rowHeaderWidth={100}
          />
        ) : (
          <div className="centered-btn">No Data Selected</div>
        )}
      </div>
      {hasData && showLabelToggles && onLabelSettingsChange && (
        <div className="control-panel">
          {/* Blocked while saving, so quick clicks cannot build on stale settings. */}
          <div
            className="control-panel-item"
            style={savingLabels ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
          >
            <span className="description">Labels:</span>
            <CheckableTag
              checked={labels.showRowHeaders}
              onChange={checked => changeLabels({ ...labels, showRowHeaders: checked })}
            >
              Row labels
            </CheckableTag>
            <CheckableTag
              checked={labels.showColHeaders}
              onChange={checked => changeLabels({ ...labels, showColHeaders: checked })}
            >
              Column labels
            </CheckableTag>
          </div>
        </div>
      )}
      <div
        className="section"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px'
        }}
      >
        <div>
          <Button danger disabled={!hasData} onClick={clearDataSelection}>
            Clear
          </Button>
        </div>
        <div
          style={{
            display: 'flex',
            gap: '8px'
          }}
        >
          {showTransform && (
            <Button
              disabled={!hasData}
              type="default"
              onClick={() => {
                setRightIsOpen(true)
              }}
            >
              Transform Data
            </Button>
          )}
          <Button disabled={!hasData} type="primary" onClick={submit}>
            Submit selection
          </Button>
        </div>
      </div>
    </div>
  )
}

export default OutputTable
